import { useEffect, useRef, useState } from "react";
import { trasActivacion } from "../../dominio/src/index.ts";

/**
 * El navegador solo deja escuchar con un reconocedor a la vez: el dictado avisa cuando empieza y termina
 * para que «Hola Donna» se pause, y las órdenes dichas durante el dictado («Hola Donna, deja de dictar»)
 * no se escriben en la historia: se entregan a «Hola Donna».
 */
export const EV_DICTADO = "donna:dictado"; // detail: { activo: boolean }
export const EV_ORDEN = "donna:orden"; // detail: { texto: string }
export const EV_DICTAR = "donna:dictar";
export const EV_DETENER = "donna:detener-dictado";
let dictadosActivos = 0;
export const hayDictadoActivo = () => dictadosActivos > 0;
function avisarDictado(activo: boolean) {
  dictadosActivos = Math.max(0, dictadosActivos + (activo ? 1 : -1));
  window.dispatchEvent(new CustomEvent(EV_DICTADO, { detail: { activo: dictadosActivos > 0 } }));
}

/**
 * Dictado con el reconocimiento de voz del navegador (Chrome, Edge, Safari; en Android e iOS vía Capacitor).
 * Entrega solo texto final; el texto provisional se muestra aparte para que el profesional vea lo que se escucha.
 */
export type Reconocedor = {
  lang: string; continuous: boolean; interimResults: boolean;
  start(): void; stop(): void;
  onresult: ((e: { resultIndex: number; results: ArrayLike<{ isFinal: boolean; 0: { transcript: string } }> }) => void) | null;
  onend: (() => void) | null; onerror: ((e: { error: string }) => void) | null;
};

export function crearReconocedor(): Reconocedor | null {
  const w = window as unknown as Record<string, new () => Reconocedor>;
  const C = w.SpeechRecognition ?? w.webkitSpeechRecognition;
  return C ? new C() : null;
}

export function useDictado(alTexto: (t: string) => void) {
  const [escuchando, setEscuchando] = useState(false);
  const [provisional, setProvisional] = useState("");
  const [error, setError] = useState("");
  const rec = useRef<Reconocedor | null>(null);
  const activo = useRef(false);
  const cb = useRef(alTexto);
  cb.current = alTexto;
  const soportado = typeof window !== "undefined" && !!((window as unknown as Record<string, unknown>).SpeechRecognition ?? (window as unknown as Record<string, unknown>).webkitSpeechRecognition);

  useEffect(() => () => { if (activo.current) avisarDictado(false); activo.current = false; rec.current?.stop(); }, []);

  function iniciar() {
    if (!soportado || activo.current) return;
    const r = crearReconocedor()!;
    r.lang = "es-EC"; r.continuous = true; r.interimResults = true;
    r.onresult = (e) => {
      let prov = "";
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const res = e.results[i];
        if (res.isFinal) {
          const t = res[0].transcript.trim();
          // «Hola Donna, …» durante el dictado es una orden, no parte de la historia
          const orden = trasActivacion(t);
          if (orden !== null) { window.dispatchEvent(new CustomEvent(EV_ORDEN, { detail: { texto: orden } })); continue; }
          cb.current(t);
        }
        else prov += res[0].transcript;
      }
      setProvisional(prov);
    };
    r.onerror = (e) => { if (e.error === "not-allowed") { setError("Permite el micrófono para dictar."); if (activo.current) avisarDictado(false); activo.current = false; } };
    // el navegador corta tras un silencio: se reanuda mientras el usuario no lo detenga
    r.onend = () => { if (activo.current) { try { r.start(); } catch { activo.current = false; avisarDictado(false); setEscuchando(false); } } else setEscuchando(false); };
    rec.current = r; activo.current = true; setError(""); setEscuchando(true);
    avisarDictado(true);
    // «Hola Donna» suelta el micrófono antes de que el dictado lo tome
    setTimeout(() => { try { if (activo.current) r.start(); } catch { /* ya empezó */ } }, 250);
  }
  function detener() { if (activo.current) avisarDictado(false); activo.current = false; rec.current?.stop(); setProvisional(""); setEscuchando(false); }
  return { soportado, escuchando, provisional, error, iniciar, detener };
}
